import { defineStore } from 'pinia';
import * as Sentry from '@sentry/vue';
import { WORKER_MSG_TOPIC } from '~/common/js/workerConfig';
import { SubscriptionInfo, PriceSubscriptionInfo } from '~/models/subscription';

export type SubscriptionState = {
  subscriptionArr: string[];
  subscriptionInfo: Record<string, SubscriptionInfo>;
  timer: NodeJS.Timeout | null;
};

export const useSubscriptionStore = defineStore('subscription', {
  state: () => ({
    // 订阅的交易对id数组，最大长度为60，超过60个会删除最早的交易对
    subscriptionArr: [] as string[],
    // 订阅的交易对信息，key为交易对id，value为交易对信息
    subscriptionInfo: {} as Record<string, SubscriptionInfo>,

    timer: null as NodeJS.Timeout | null,
  }),
  actions: {
    SET_SUBSCRIPTION_ARR(payload: string[]) {
      this.subscriptionArr = payload;
    },
    SET_SUBSCRIPTION_INFO(payload: Record<string, SubscriptionInfo>) {
      this.subscriptionInfo = payload;
    },
    // 更新订阅的交易对信息
    UPDATE_PRICE_LIST(payload: PriceSubscriptionInfo[]) {
      const { subscriptionInfo } = this;

      payload.forEach(({ data, symbolId }: PriceSubscriptionInfo) => {
        subscriptionInfo[symbolId] = {
          ...data,
          symbol: symbolId,
        };
      });
      this.subscriptionInfo = { ...subscriptionInfo };
    },
    // 更新单个交易对信息
    SET_SUBSCRIBE_PRICE_SYMBOL_INFO(payload: SubscriptionInfo) {
      const { subscriptionInfo } = this;
      const { symbol, ...others } = payload;
      subscriptionInfo[symbol] = {
        ...others,
        symbol,
      };
      this.subscriptionInfo = { ...subscriptionInfo };
    },
    // 设置定时器

    SET_TIMER(payload: NodeJS.Timeout | null) {
      this.timer = payload;
    },
    /**
     * 订阅多个币种实时价格，单币种订阅也适用，只是传入的是[单个币种id]，注意这里订阅的都是美元价格
     * @param {*} param0
     * @param {*} payload
     * @description payload.symbolIds 为多个币种id，['53', '1012']
     * @description payload.fixSymbolIds 为固定的币种id，不会在超过的时候被推出队列，['53', '1012']
     * @returns
     */
    subscribeCoins(payload) {
      // if (!state.symbolList || state.symbolList.length === 0) {
      //   console.log('no trade pair')
      //   return
      // }
      if (!payload || !Array.isArray(payload.symbolIds) || payload.symbolIds.length === 0) {
        console.log('no symbolIds');
        return;
      }
      const nuxtApp = useNuxtApp();
      const SUB_ARR_MAX_LENGTH = 60;
      const { symbolIds, fixSymbolIds } = payload;
      // 将symbolIds转为字符串数组，避免出现数字和字符串混合的情况
      let addSymbolIds = symbolIds.map((id: string) => id.toString());

      // 拼接fixSymbolIds和symbolIds，注意去重
      if (fixSymbolIds && Array.isArray(fixSymbolIds) && fixSymbolIds.length > 0) {
        addSymbolIds = Array.from(new Set(symbolIds.concat(fixSymbolIds.map((id: string) => id.toString()))));
      }
      const { subscriptionArr, subscriptionInfo } = this;
      const newSubscriptionArr: string[] = [...subscriptionArr];
      // addSymbolIds中剔除掉已经订阅的币种
      const finalAddSymbolIds: string[] = [];
      const alreadySubscribedSymbolIdInfo: Record<string, boolean> = {};
      // 已经订阅的数组下标arr
      const alreadySubscribedArrIndexArr: number[] = [];
      addSymbolIds.forEach((id: string, index: number) => {
        if (!subscriptionArr.includes(id)) {
          finalAddSymbolIds.push(id);
        } else {
          alreadySubscribedSymbolIdInfo[id] = true;
          alreadySubscribedArrIndexArr.push(index);
        }
      });

      if (alreadySubscribedArrIndexArr.length !== 0) {
        // 将alreadySubscribedArrIndexArr中的下标从大到小排序，newSubscriptionArr中删除掉这些下标对应的元素，然后添加到newSubscriptionArr的最后面
        alreadySubscribedArrIndexArr.sort((a: number, b: number) => b - a);
        alreadySubscribedArrIndexArr.forEach((index: number) => {
          newSubscriptionArr.push(newSubscriptionArr.splice(index, 1)[0]);
        });
      }

      // 判断当前数组加上finalAddSymbolIds是否超过60个
      if (newSubscriptionArr.length + finalAddSymbolIds.length <= SUB_ARR_MAX_LENGTH) {
        // 不超过60个，直接添加到newSubscriptionArr
        this.SET_SUBSCRIPTION_ARR(newSubscriptionArr.concat(finalAddSymbolIds));
      } else {
        // 超过60个，先删除掉超过的部分，注意先进先出但是不能删除在alreadySubscribedSymbolIdInfo中的
        const needDeleteNum: number = newSubscriptionArr.length + finalAddSymbolIds.length - SUB_ARR_MAX_LENGTH;
        let deleteCount: number = 0;
        const delSymbolIds: string[] = [];
        // 从subscriptionArr中删除掉needDeleteNum个
        while (deleteCount < needDeleteNum && newSubscriptionArr.length > 0) {
          // 如果subscriptionArr[0]在alreadySubscribedSymbolIdInfo中，那么就不删除，否则删除
          if (!alreadySubscribedSymbolIdInfo[newSubscriptionArr[0]]) {
            delSymbolIds.push(newSubscriptionArr[0]);
            newSubscriptionArr.shift();
            deleteCount++;
          } else {
            newSubscriptionArr.push(newSubscriptionArr.shift() as string);
          }
        }
        this.SET_SUBSCRIPTION_ARR(newSubscriptionArr.concat(finalAddSymbolIds));
        // subscriptionInfo中删除掉超过的部分
        delSymbolIds.forEach((id: string) => {
          delete subscriptionInfo[id];
        });
        this.SET_SUBSCRIPTION_INFO({ ...subscriptionInfo });
        // 发起取消订阅
        this.unsubscribeCoins({ symbolIds: delSymbolIds });
      }
      if (finalAddSymbolIds.length === 0) {
        return;
      }
      nuxtApp.$worker.ws.emit('message', {
        type: WORKER_MSG_TOPIC.get('SUBSCRIBE'),
        payload: [
          'price_batch',
          {
            symbol: finalAddSymbolIds.join(','),
            local: 'en_US',
          },
          false,
        ],
      });
      if (this.timer) {
        clearTimeout(this.timer);
        this.SET_TIMER(null);
      }
      // 定时器5s后检查所有订阅的交易对是否已经收到价格数据，没有收到的数据的交易对需要上报异常给sentry
      const timer = setTimeout(() => {
        const { subscriptionInfo } = this;
        const notReceivedSymbolIds: string[] = [];
        finalAddSymbolIds.forEach((id: string) => {
          if (!subscriptionInfo[id]?.c) {
            notReceivedSymbolIds.push(id);
          }
        });
        if (notReceivedSymbolIds.length > 0) {
          Sentry.captureMessage('spot_subscribed_symbol_no_price', {
            level: 'warning',
            extra: {
              symbols: notReceivedSymbolIds.join(','),
            },
          });
        }
      }, 5000);
      this.SET_TIMER(timer);
    },

    // 取消订阅多个币种实时价格, 单币种取消订阅也适用，只是传入的是[单个币种id]
    unsubscribeCoins(payload) {
      if (!payload || !Array.isArray(payload.symbolIds) || payload.symbolIds.length === 0) {
        console.log('no symbolIds');
        return;
      }
      const nuxtApp = useNuxtApp();
      const { symbolIds } = payload;
      nuxtApp.$worker.ws.emit('message', {
        type: WORKER_MSG_TOPIC.get('UNSUBSCRIBE'),
        payload: [
          'price_batch',
          {
            symbol: symbolIds.join(','),
            local: 'en_US',
          },
          true,
        ],
      });
    },

    // 取消当前所有价格订阅
    unsubscribeAllCoin() {
      const { subscriptionArr } = this;
      if (subscriptionArr.length === 0) {
        return;
      }
      this.unsubscribeCoins({ symbolIds: subscriptionArr });
      this.SET_SUBSCRIPTION_ARR([]);
      this.SET_SUBSCRIPTION_INFO({});
      // 清除定时器
      if (this.timer) {
        clearTimeout(this.timer);
        this.SET_TIMER(null);
      }
    },
  },
});
